import { useState, useEffect } from 'react';
import { 
  agendaItems, 
  analysisItems, 
  companiesList, 
  pipelineData 
} from '../data/centralizedData';

export const useSearch = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.trim()) {
        performSearch(searchTerm);
      } else {
        setSearchResults([]);
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Helper function to safely check if a value includes the search query
  const safeIncludes = (value, query) => {
    if (!value) return false;
    if (typeof value === 'string') {
      return value.toLowerCase().includes(query.toLowerCase());
    }
    if (typeof value === 'number') {
      return value.toString().includes(query);
    }
    return false;
  };

  const performSearch = (query) => {
    setIsSearching(true);
    const results = [];
    const searchQuery = query.toLowerCase();

    // Search agenda items - with safety checks
    try {
      if (agendaItems && Array.isArray(agendaItems)) {
        agendaItems.forEach(item => {
          if (!item) return; // Skip if item is undefined/null
          
          if (safeIncludes(item.title, searchQuery) || safeIncludes(item.id, searchQuery)) {
            results.push({
              type: 'section',
              title: item.title || 'Unnamed Section',
              subtitle: `Board Meeting Section • ${item.duration || 'N/A'}`,
              id: item.id || 'unknown',
              icon: item.icon || 'FileText'
            });
          }
        });
      }
    } catch (error) {
      console.warn('Error searching agenda items:', error);
    }

    // Search portfolio companies - with safety checks
    try {
      if (companiesList && Array.isArray(companiesList)) {
        companiesList.forEach((company, index) => {
          if (!company || typeof company !== 'object') return; // Skip if company is undefined/null/not an object
          
          let matchedIn = [];

          if (safeIncludes(company.name, searchQuery)) {
            matchedIn.push('name');
          }
          if (safeIncludes(company.sector, searchQuery)) {
            matchedIn.push('sector');
          }
          if (safeIncludes(company.status, searchQuery)) {
            matchedIn.push('status');
          }
          if (safeIncludes(company.integration, searchQuery)) {
            matchedIn.push('integration');
          }
          if (safeIncludes(company.id, searchQuery)) {
            matchedIn.push('id');
          }

          if (matchedIn.length > 0) {
            // Safely generate company ID
            let companyId;
            try {
              companyId = company.id || 
                         (company.name && typeof company.name === 'string' ? 
                          company.name.toLowerCase().replace(/\s+/g, '-') : 
                          `company-${index}`);
            } catch (error) {
              companyId = `company-${index}`;
            }

            results.push({
              type: 'company',
              title: company.name || 'Unnamed Company',
              subtitle: `${company.sector || 'Unknown Sector'} • ${company.status || 'Unknown Status'} • Found in: ${matchedIn.join(', ')}`,
              id: companyId,
              icon: company.icon || 'Building'
            });
          }
        });
      }
    } catch (error) {
      console.warn('Error searching portfolio companies:', error);
    }

    // Search pipeline companies - with safety checks
    try {
      if (pipelineData?.companies && Array.isArray(pipelineData.companies)) {
        pipelineData.companies.forEach((company, index) => {
          if (!company) return; // Skip if company is undefined/null
          
          let matchedIn = [];

          if (safeIncludes(company.name, searchQuery)) {
            matchedIn.push('name');
          }
          if (safeIncludes(company.sector, searchQuery)) {
            matchedIn.push('sector');
          }
          if (safeIncludes(company.description, searchQuery)) {
            matchedIn.push('description');
          }
          if (safeIncludes(company.stage, searchQuery)) {
            matchedIn.push('stage');
          }
          if (safeIncludes(company.priority, searchQuery)) {
            matchedIn.push('priority');
          }

          if (matchedIn.length > 0) {
            results.push({
              type: 'pipeline',
              title: company.name || 'Unnamed Company',
              subtitle: `Pipeline: ${company.sector || 'Unknown Sector'} • ${company.stage || 'Unknown Stage'} • Found in: ${matchedIn.join(', ')}`,
              id: 'pipeline',
              icon: 'TrendingUp'
            });
          }
        });
      }
    } catch (error) {
      console.warn('Error searching pipeline companies:', error);
    }

    // Search analysis items - with safety checks
    try {
      if (analysisItems && Array.isArray(analysisItems)) {
        analysisItems.forEach(item => {
          if (!item) return; // Skip if item is undefined/null
          
          let matchedIn = [];

          if (safeIncludes(item.title, searchQuery)) {
            matchedIn.push('title');
          }
          if (safeIncludes(item.category, searchQuery)) {
            matchedIn.push('category');
          }
          if (safeIncludes(item.description, searchQuery)) {
            matchedIn.push('description');
          }
          if (safeIncludes(item.status, searchQuery)) {
            matchedIn.push('status');
          }

          if (matchedIn.length > 0) {
            // Safely generate analysis ID
            let analysisId;
            try {
              analysisId = item.id || 
                          (item.title && typeof item.title === 'string' ? 
                           item.title.toLowerCase().replace(/\s+/g, '-') : 
                           'unknown-analysis');
            } catch (error) {
              analysisId = 'unknown-analysis';
            }

            results.push({
              type: 'analysis',
              title: item.title || 'Unnamed Analysis',
              subtitle: `${item.category || 'Analysis'} • ${item.status || 'Unknown Status'} • Found in: ${matchedIn.join(', ')}`,
              id: analysisId,
              icon: 'FileText'
            });
          }
        });
      }
    } catch (error) {
      console.warn('Error searching analysis items:', error);
    }

    setSearchResults(results);
    setIsSearching(results.length > 0 || searchQuery.length > 0);
  };

  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
  };

  const handleSearchResultClick = (result) => {
    // Return the result object so parent component can handle navigation
    return result;
  };

  return {
    searchTerm,
    searchResults,
    isSearching,
    handleSearch,
    handleSearchResultClick
  };
};